import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "@/components/common/Logo";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/restaurant", label: "Restaurant" },
  { to: "/hotel", label: "Hotel" },
  { to: "/combo", label: "Combo" },
  { to: "/enterprise", label: "Enterprise" },
  { to: "/features", label: "Funzionalità" },
  { to: "/pricing", label: "Prezzi" },
];

// In production marketing site (.com) this points to https://costcontrolx.app.
// On the preview/dev pod it is empty → CTA stays same-origin so links work.
const APP_URL = (process.env.REACT_APP_APP_URL || "").replace(/\/$/, "");

export default function MarketingNavbar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isHome = location.pathname === "/";
  const overDark = isHome && !scrolled;

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      // Preview/prod pod: html/body hanno `overflow-x: hidden` che a volte
      // fa restare window.scrollY = 0. Leggiamo lo scroll da entrambi i
      // sorgenti standard per essere robusti.
      const y = window.scrollY
        || document.documentElement.scrollTop
        || document.body.scrollTop
        || 0;
      setScrolled(y > 12);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <header
      data-testid="marketing-navbar"
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-[#E2E8F0]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <Link
          to="/"
          className={`inline-flex items-center rounded-lg transition-colors ${
            scrolled
              ? ""
              : "bg-white/95 px-2.5 py-1.5 border border-white/70 shadow-sm"
          }`}
          data-testid="navbar-brand"
        >
          <Logo variant="full" size="md" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `px-3.5 h-9 inline-flex items-center text-sm font-semibold rounded-md transition-colors ${
                  isActive
                    ? overDark
                      ? "text-white bg-white/15"
                      : "text-[#0B1B2C] bg-[#E0F2F1]"
                    : overDark
                      ? "text-white hover:text-white hover:bg-white/10"
                      : "text-[#374151] hover:text-[#0B1B2C] hover:bg-[#F5F3EE]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden sm:flex items-center gap-2.5">
          <a
            href={`${APP_URL}/login`}
            className="px-3.5 h-9 inline-flex items-center text-sm font-semibold rounded-md border bg-[#F5F3EE] hover:bg-[#EFEEE9] active:bg-[#E7E5E0] border-[#D8D4CB] text-[#102A43] shadow-sm transition-colors"
            data-testid="navbar-login"
          >
            Accedi
          </a>
          <a
            href={`${APP_URL}/pricing`}
            className={`h-9 px-4 inline-flex items-center text-sm font-bold rounded-md shadow-sm transition-colors ${
              scrolled
                ? "bg-[#102A43] hover:bg-[#0B1B2C] text-white border border-[#102A43]"
                : "bg-[#0D9488] hover:bg-[#0F766E] text-white border border-[#0D9488]"
            }`}
            data-testid="navbar-cta"
          >
            Inizia prova gratuita
          </a>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg border transition-colors relative z-10 shrink-0 bg-[#F5F3EE] hover:bg-[#EFEEE9] active:bg-[#E7E5E0] border-[#D8D4CB] text-[#102A43] shadow-sm"
          aria-label="Apri menu"
          data-testid="mobile-menu-btn"
        >
          {open ? <X className="w-5 h-5" strokeWidth={2.4} /> : <Menu className="w-5 h-5" strokeWidth={2.4} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E2E8F0] bg-white shadow-sm">
          <div className="px-5 py-3 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-semibold ${
                    isActive
                      ? "text-[#0B1B2C] bg-[#E0F2F1]"
                      : "text-[#374151] hover:bg-[#F5F3EE]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="border-t border-[#E2E8F0] mt-2 pt-3 grid grid-cols-2 gap-2">
              <a
                href={`${APP_URL}/login`}
                className="text-center px-3 py-2 text-sm font-semibold border border-[#D8D4CB] bg-[#F5F3EE] text-[#102A43] rounded-md"
              >
                Accedi
              </a>
              <a
                href={`${APP_URL}/pricing`}
                className="text-center px-3 py-2 text-sm font-bold bg-[#102A43] text-white rounded-md"
              >
                Prova gratuita
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
